import React, { useRef } from "react";
import { TextInput } from "react-native";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { AlertCircleIcon } from "lucide-react-native";
import { useForm, useFormContext, UseFormReturn } from "react-hook-form";

import { unemploymentCompensationStatusEnumSchema } from "@libs/api/patients";
import { VnAlert, VnAlertTitle } from "@/components/VnAlert";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCurrencyInput } from "@/components/VnCurrencyInput";
import { VnForm } from "@/components/VnForm";
import { VnPicker } from "@/components/VnPicker/VnPicker";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  EmployedFormType,
  getIncomeTypeAlertDescriptionI18nKey,
  getIncomeTypeI18nKey,
  householdMemberPayFrequency,
  HouseholdMemberType,
  householdMemberUnemploymentCompensationStatus,
  householdMemberWorkAndIncomeFormSchema,
  INCOME_NUMERIC_FIELDS,
  IncomeTypeEnum,
  SelfEmployedFormType,
  UnemployedFormType,
} from "@/domain/householdMember";
import { useWorkAndIncomeForms } from "@/hooks/useWorkAndIncomeForms";
import { transformFormValues } from "@/utils/householdMember";

import { ScreenLayout } from "./ScreenLayout";

const EmployedForm = ({
  member,
  form,
  onClose,
  isEditMode,
}: {
  member: HouseholdMemberType;
  form: UseFormReturn<EmployedFormType>;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const grossMonthlyIncomeRef = useRef<TextInput>(null);
  const workWeekHoursRef = useRef<TextInput>(null);
  const employerNameRef = useRef<TextInput>(null);
  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnCurrencyInput
        isRequired
        control={form.control}
        name="grossMonthlyIncome"
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.grossMonthlyIncome"
        ref={grossMonthlyIncomeRef}
        returnKeyType="next"
        onSubmitEditing={() => workWeekHoursRef.current?.focus()}
      />
      <VnTextInput
        keyboardType="number-pad"
        isRequired
        name="workWeekHours"
        control={form.control}
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.workWeekHours"
        ref={workWeekHoursRef}
        returnKeyType="next"
        onSubmitEditing={() => employerNameRef.current?.focus()}
      />
      <VnPicker
        isRequired
        name="payFrequency"
        control={form.control}
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.payFrequency"
        options={householdMemberPayFrequency}
      />
      <VnTextInput
        isRequired
        name="employerName"
        control={form.control}
        placeholderI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.enterName"
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.employerName"
        ref={employerNameRef}
        returnKeyType="done"
      />
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

const UnemployedForm = ({
  member,
  form,
  onClose,
  isEditMode,
}: {
  member: HouseholdMemberType;
  form: UseFormReturn<UnemployedFormType>;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const unemploymentCompensationStatus = form.watch(
    "unemploymentCompensationStatus",
  );

  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnRadioGroupInput
        name="unemploymentCompensationStatus"
        control={form.control}
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.unemploymentCompensationStatus"
        options={householdMemberUnemploymentCompensationStatus}
        className="gap-6"
        radioGroupClassName="gap-6"
      />
      {unemploymentCompensationStatus ===
        unemploymentCompensationStatusEnumSchema.enum
          .UNEMPLOYMENT_COMPENSATED && (
        <VnBox className="border-border bg-secondary gap-2.5 rounded-md border p-4">
          <VnCurrencyInput
            isRequired={false}
            control={form.control}
            name="unemploymentCompensationAmount"
            labelI18nKey="native:applicationFlow.amountPerMonth"
          />
        </VnBox>
      )}
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

const SelfEmployedForm = ({
  member,
  form,
  onClose,
  isEditMode,
}: {
  member: HouseholdMemberType;
  form: UseFormReturn<SelfEmployedFormType>;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const selfEmploymentNetMonthlyIncomeRef = useRef<TextInput>(null);
  const employmentStatusSelfEmployedDetailsRef = useRef<TextInput>(null);
  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnCurrencyInput
        isRequired
        control={form.control}
        name="selfEmploymentNetMonthlyIncome"
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.selfEmploymentNetMonthlyIncome"
        ref={selfEmploymentNetMonthlyIncomeRef}
        onSubmitEditing={() =>
          employmentStatusSelfEmployedDetailsRef.current?.focus()
        }
        returnKeyType="next"
      />
      <VnTextInput
        isRequired
        ref={employmentStatusSelfEmployedDetailsRef}
        name="employmentStatusSelfEmployedDetails"
        control={form.control}
        labelI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.typeOfWork"
        placeholderI18nKey="native:applicationWorkAndIncomeScreen.memberDialog.enterTypeOfWork"
        returnKeyType="done"
      />
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

// Mapping for simple income forms (only amount field)
const simpleIncomeFormFieldMap: Record<
  IncomeTypeEnum,
  SimpleIncomeFormPropsType["fieldName"] | null
> = {
  [IncomeTypeEnum.EMPLOYED]: null,
  [IncomeTypeEnum.UNEMPLOYED]: null,
  [IncomeTypeEnum.SELF_EMPLOYED]: null,
  [IncomeTypeEnum.PENSION]: "pensionMonthlyIncome",
  [IncomeTypeEnum.SSA]: "ssaMonthlyIncome",
  [IncomeTypeEnum.SSDI]: "ssdiMonthlyIncome",
  [IncomeTypeEnum.ALIMONY]: "alimonyMonthlyIncome",
  [IncomeTypeEnum.RETIREMENT_INCOME]: "retirementMonthlyIncome",
  [IncomeTypeEnum.PROPERTY_RENTAL_INCOME]: "propertyRentalMonthlyIncome",
  [IncomeTypeEnum.WORKERS_COMPENSATION]: "workersCompensationMonthlyIncome",
  [IncomeTypeEnum.OTHER]: "otherMonthlyIncome",
  [IncomeTypeEnum.NONE]: null,
};

// Generic form component for simple income types with only one amount field
type SimpleIncomeFormPropsType = {
  member: HouseholdMemberType;
  onClose: () => void;
  fieldName:
    | "pensionMonthlyIncome"
    | "ssaMonthlyIncome"
    | "ssdiMonthlyIncome"
    | "alimonyMonthlyIncome"
    | "retirementMonthlyIncome"
    | "propertyRentalMonthlyIncome"
    | "workersCompensationMonthlyIncome"
    | "otherMonthlyIncome";
  isEditMode: boolean;
};

const SimpleIncomeForm = ({
  member,
  onClose,
  fieldName,
  isEditMode,
}: SimpleIncomeFormPropsType) => {
  type FormType = Record<typeof fieldName, string | null>;
  const formSchema = householdMemberWorkAndIncomeFormSchema.pick({
    [fieldName]: true,
  } as Record<typeof fieldName, true>);

  const form = useForm<FormType>({
    defaultValues: {
      [fieldName]: member[fieldName] ? String(member[fieldName]) : null,
    },
    resolver: standardSchemaResolver(formSchema),
  });

  return (
    <VnForm form={form} isInputOnlyForm className="flex-1 flex-col gap-6">
      <VnCurrencyInput
        isRequired={false}
        control={form.control}
        name={fieldName}
        labelI18nKey="native:applicationFlow.amountPerMonth"
      />
      <FormFooter
        memberId={member.id}
        onClose={onClose}
        isEditMode={isEditMode}
      />
    </VnForm>
  );
};

const FormFooter = ({
  memberId,
  onClose,
  isEditMode,
}: {
  memberId: string;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const form = useFormContext();

  const {
    mutate: updateHouseholdMember,
    isPending: isUpdatingHouseholdMember,
  } = useUpdateHouseholdMemberMutation();
  const handleClose = () => {
    onClose();
    form.reset();
  };

  const handleDelete = (formValues: Record<string, unknown>) => {
    const transformedValues = { ...formValues };

    for (const field in transformedValues) {
      transformedValues[field] = null;
    }

    updateHouseholdMember(
      {
        id: memberId,
        householdMemberData: transformedValues,
      },
      {
        onSuccess: handleClose,
      },
    );
  };

  const handleSave = (formValues: Record<string, unknown>) => {
    const values = { ...formValues };
    if (
      values.unemploymentCompensationStatus ===
      unemploymentCompensationStatusEnumSchema.enum.NOT_UNEMPLOYMENT_COMPENSATED
    ) {
      values.unemploymentCompensationAmount = null;
    }
    const transformedValues = transformFormValues(
      values,
      INCOME_NUMERIC_FIELDS,
    );
    updateHouseholdMember(
      {
        id: memberId,
        householdMemberData: { ...transformedValues, hasIncome: true },
      },
      {
        onSuccess: handleClose,
      },
    );
  };
  return (
    <VnBox className="mt-auto flex shrink-0 flex-row justify-between gap-2 px-0">
      {isEditMode && (
        <VnButton
          variant="destructive"
          size="lg"
          i18nKey="common:delete"
          onPress={() => {
            handleDelete(form.getValues());
          }}
          isLoading={isUpdatingHouseholdMember}
          className="flex-1 sm:flex-none"
        />
      )}
      <VnButton
        size="lg"
        variant="outline"
        i18nKey="common:cancel"
        onPress={handleClose}
        className="flex-1 sm:ml-auto sm:flex-none"
      />
      <VnButton
        size="lg"
        i18nKey="common:save"
        disabled={!form.formState.isDirty || !form.formState.isValid}
        onPress={() => {
          void form.handleSubmit(handleSave)();
        }}
        className="flex-1 sm:flex-none"
        isLoading={isUpdatingHouseholdMember}
      />
    </VnBox>
  );
};

type WorkAndIncomeFormPropsType = {
  member: HouseholdMemberType;
  selectedIncomeType: IncomeTypeEnum;
  onClose: () => void;
  isEditMode: boolean;
};

export const WorkAndIncomeForm = ({
  member,
  selectedIncomeType,
  onClose,
  isEditMode,
}: WorkAndIncomeFormPropsType) => {
  const { employedForm, unemployedForm, selfEmployedForm } =
    useWorkAndIncomeForms(member);

  const fieldName = simpleIncomeFormFieldMap[selectedIncomeType];

  return (
    <VnBox className="flex-1 flex-col">
      <VnText
        variant="lg"
        className="text-background mb-2 shrink-0 text-center"
        i18nKey={getIncomeTypeI18nKey(selectedIncomeType)}
      />
      <ScreenLayout
        variant="FORM"
        backgroundClassName="bg-background"
        hasHorizontalPadding={false}>
        <VnBox className="flex-1 flex-col gap-6 px-4 py-1">
          <VnAlert
            icon={AlertCircleIcon}
            className="bg-secondary"
            iconClassName="text-muted-foreground size-5 mt-0.5">
            <VnAlertTitle
              className="font-primary text-muted-foreground text-base leading-6 tracking-normal"
              i18nKey={getIncomeTypeAlertDescriptionI18nKey(selectedIncomeType)}
            />
          </VnAlert>
          {selectedIncomeType === IncomeTypeEnum.EMPLOYED && (
            <EmployedForm
              member={member}
              form={employedForm}
              onClose={onClose}
              isEditMode={isEditMode}
            />
          )}
          {selectedIncomeType === IncomeTypeEnum.UNEMPLOYED && (
            <UnemployedForm
              member={member}
              form={unemployedForm}
              onClose={onClose}
              isEditMode={isEditMode}
            />
          )}
          {selectedIncomeType === IncomeTypeEnum.SELF_EMPLOYED && (
            <SelfEmployedForm
              member={member}
              form={selfEmployedForm}
              onClose={onClose}
              isEditMode={isEditMode}
            />
          )}
          {fieldName && (
            <SimpleIncomeForm
              member={member}
              onClose={onClose}
              fieldName={fieldName}
              isEditMode={isEditMode}
            />
          )}
        </VnBox>
      </ScreenLayout>
    </VnBox>
  );
};
